<template>
	<div class="login-view" :class="configStore.electronMode ? 'electron' : ''">
		<AuthBackground v-if="!configStore.electronMode" />
		<div class="login-shell">
			<AuthBrandPanel />
			<main class="form-panel">
				<div class="form-header">
					<h1 class="form-title">{{ $t('login.pageTitle') }}</h1>
				</div>
				<div class="mode-nav">
					<button type="button" class="mode-nav-item" :class="{ active: loginMode === 'password' }"
						@click="goBackToPassword">
						<i class="el-icon-lock"></i>
						<span>{{ $t('login.tabPassword') }}</span>
					</button>
					<button type="button" class="mode-nav-item" :class="{ active: loginMode === 'qr' }"
						@click="switchToQrLogin">
						<i class="iconfont icon-scan"></i>
						<span>{{ $t('login.tabQr') }}</span>
					</button>
				</div>

				<el-form v-if="loginMode === 'password'" class="form" :model="loginForm" status-icon :rules="rules"
					ref="loginForm" @keyup.enter.native="submitForm()" v-loading="loading"
					:element-loading-text="$t('login.logging')">
					<div class="form-body">
						<el-form-item prop="terminal" v-show="false">
							<el-input type="terminal" v-model="loginForm.terminal"></el-input>
						</el-form-item>
						<el-form-item prop="userName" :label="$t('login.userName')">
							<el-input type="userName" v-model="loginForm.userName" :placeholder="loginNamePlaceholder"
								prefix-icon="el-icon-user"></el-input>
						</el-form-item>
						<el-form-item prop="password" :label="$t('login.password')">
							<el-input type="password" v-model="loginForm.password" :placeholder="$t('login.password')"
								prefix-icon="el-icon-lock"></el-input>
						</el-form-item>
						<div class="form-options">
							<el-checkbox v-model="isAutoLogin">{{ $t('login.autoLogin') }}</el-checkbox>
							<router-link v-if="containsPhoneMode || containsEmailMode" class="option-link"
								to="/password/reset">{{ $t('login.forgotPwd') }}</router-link>
						</div>
						<el-button class="submit-btn" type="primary" @click="submitForm()">{{ $t('login.login') }}</el-button>
						<router-link v-if="isEnableDemo" class="demo-login-link" to="/login/demo">
							{{ $t('login.demoLogin') }}
						</router-link>
					</div>
				</el-form>

				<QrLogin v-else embedded @back="goBackToPassword" />

				<div class="form-footer">
					<span class="footer-text">{{ $t('login.noAccount') }}</span>
					<router-link class="footer-link" to="/register">{{ $t('login.registerNow') }}</router-link>
				</div>
			</main>
		</div>
		<!-- <icp></icp> -->
		<captcha-image ref="captchaRef"></captcha-image>
	</div>
</template>

<script>
import AuthBackground from '../components/common/AuthBackground.vue'
import AuthBrandPanel from '../components/common/AuthBrandPanel.vue'
import CaptchaImage from '../components/common/CaptchaImage.vue'
import QrLogin from '../components/login/QrLogin.vue'
import * as auth from '../api/auth.js'

export default {
	name: "login",
	components: {
		AuthBackground,
		AuthBrandPanel,
		CaptchaImage,
		QrLogin
	},
	data() {
		var checkUsername = (rule, value, callback) => {
			if (!value) {
				return callback(new Error(this.$t('login.inputUsername')));
			}
			callback();
		};
		var checkPassword = (rule, value, callback) => {
			if (value === '') {
				callback(new Error(this.$t('login.inputPassword')));
			}
			callback();
		};
		return {
			loading: false,
			isAutoLogin: true,
			loginForm: {
				terminal: this.$enums.TERMINAL_TYPE.WEB,
				userName: '',
				password: ''
			},
			rules: {
				userName: [{
					validator: checkUsername,
					trigger: 'blur'
				}],
				password: [{
					validator: checkPassword,
					trigger: 'blur'
				}]
			},
			loginMode: 'password'
		};
	},
	methods: {
		submitForm() {
			this.$refs.loginForm.validate((valid) => {
				if (valid) {
					this.loading = true;
					this.$http({
						url: "/login",
						method: 'post',
						data: this.loginForm
					}).then((data) => {
						auth.saveLoginSession(data, {
							autoLogin: this.isAutoLogin,
							userName: this.loginForm.userName
						});
						this.$message.success(this.$t('login.loginSuccess'));
						this.$router.push("/home/chat");
					}).finally(() => {
						this.loading = false
					})
				}
			});
		},
		switchToQrLogin() {
			this.loginMode = 'qr';
		},
		goBackToPassword() {
			this.loginMode = 'password';
		},
		tryTokenAutoLogin() {
			if (!auth.isAutoLoginEnabled()) {
				return;
			}
			this.loading = true;
			auth.refreshLogin(this.$http).then(() => {
				this.$router.push("/home/chat");
			}).catch(() => {
				auth.clearLoginSession(true);
				this.isAutoLogin = false;
			}).finally(() => {
				this.loading = false;
			});
		}
	},
	mounted() {
		auth.removeLegacyPassword();
		if (localStorage.getItem("isAutoLogin") != null) {
			this.isAutoLogin = auth.isAutoLoginEnabled();
		}
		this.loginForm.userName = auth.getSavedUsername();
		this.configStore.loadConfig();
		this.tryTokenAutoLogin();
	},
	computed: {
		loginNamePlaceholder() {
			let mode = this.configStore.registration.mode;
			let strText = this.$t('login.userName');
			if (mode.includes("phone")) {
				strText += "/" + this.$t('register.phone')
			}
			if (mode.includes("email")) {
				strText += "/" + this.$t('register.email')
			}
			return strText;
		},
		containsPhoneMode() {
			return this.modes.includes("phone")
		},
		containsEmailMode() {
			return this.modes.includes("email")
		},
		modes() {
			return this.configStore.registration.mode || []
		},
		isEnableDemo() {
			return process.env.VUE_APP_IS_ENABLE_DEMO == 'true';
		}
	}
}
</script>

<style scoped lang="scss">
.login-view {
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	height: 100%;
	background: #f3f7ff;
	overflow: auto;
	padding: 32px 24px;
	box-sizing: border-box;

	.login-shell {
		position: relative;
		z-index: 1;
		display: flex;
		width: 100%;
		max-width: 920px;
		min-height: 560px;
		background: #fff;
		border-radius: 20px;
		box-shadow: 0 24px 64px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.04);
		overflow: hidden;
	}

	&.electron {
		background: #fff;
		padding-top: 25px;
		box-sizing: border-box;
		height: 100%;
		overflow: hidden;

		.login-shell {
			width: 100%;
			height: 100%;
			max-width: none;
			min-height: 0;
			border-radius: 0;
			box-shadow: none;
		}

		:deep(.auth-brand-panel) {
			flex: 0 0 380px;
			padding: 36px 32px;
		}

		.form-panel {
			flex: 1;
			padding: 24px 32px;
			justify-content: center;
			overflow-y: auto;
		}

		.form-header {
			margin-bottom: 16px;
		}

		.mode-nav {
			margin-bottom: 16px;
		}

		.form-body {
			::v-deep .el-form-item {
				margin-bottom: 14px;
			}
		}

		.form-footer {
			margin-top: 16px;
			padding-top: 16px;
		}
	}

	@media (max-width: 860px) {
		&:not(.electron) {
			padding: 16px;

			.login-shell {
				flex-direction: column;
				max-width: 440px;
				min-height: auto;
			}

			:deep(.auth-brand-panel) {
				display: none;
			}

			.form-panel {
				padding: 28px 24px 32px;
			}
		}
	}

	.form-panel {
		flex: 1;
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding: 40px 44px;
		min-width: 0;
	}

	.form-header {
		margin-bottom: 24px;
		max-width: 380px;
		width: 100%;
		margin-left: auto;
		margin-right: auto;
	}

	.form-title {
		margin: 0;
		font-size: 24px;
		font-weight: 700;
		line-height: 1.3;
		color: #152443;
	}

	.mode-nav {
		display: flex;
		gap: 4px;
		margin: 0 auto 24px;
		max-width: 380px;
		width: 100%;
		border-bottom: 1px solid #d9e0f0;
	}

	.mode-nav-item {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 0 4px 12px;
		margin-right: 20px;
		border: none;
		background: transparent;
		cursor: pointer;
		font-size: 14px;
		color: var(--im-text-color-lighter);
		position: relative;
		transition: color 0.2s ease;

		.iconfont {
			font-size: 15px;
		}

		&::after {
			content: '';
			position: absolute;
			left: 0;
			right: 0;
			bottom: -1px;
			height: 2px;
			background: var(--im-color-primary);
			border-radius: 2px 2px 0 0;
			transform: scaleX(0);
			transition: transform 0.2s ease;
		}

		&.active {
			color: var(--im-color-primary);
			font-weight: 600;

			&::after {
				transform: scaleX(1);
			}
		}

		&:hover:not(.active) {
			color: var(--im-text-color-light);
		}
	}

	.form {
		width: 100%;
		max-width: 380px;
		margin: 0 auto;
	}

	.form-body {
		::v-deep .el-form-item {
			margin-bottom: 18px;

			.el-form-item__label {
				float: none;
				display: block;
				text-align: left;
				line-height: 1.4;
				padding: 0 0 6px;
				font-size: 13px;
				font-weight: 500;
				color: #152443;
			}

			.el-form-item__content {
				line-height: 1;
			}

			.el-input__inner {
				height: 44px;
				border-radius: 10px;
				border: 1px solid #d9e0f0;
				background: #fafbfd;
				padding-left: 40px;
				font-size: 14px;
				transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

				&:hover {
					border-color: #c7d4e8;
				}

				&:focus {
					border-color: var(--im-color-primary);
					background: #fff;
					box-shadow: 0 0 0 3px rgba(65, 105, 222, 0.12);
				}

				&::placeholder {
					color: var(--im-text-color-lighter-extra);
				}
			}

			.el-input__prefix {
				left: 12px;

				.el-input__icon {
					color: #94a3b8;
					font-size: 16px;
					line-height: 44px;
				}
			}
		}
	}

	.form-options {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 8px;

		::v-deep .el-checkbox__label {
			color: var(--im-text-color-lighter);
			font-size: 13px;
		}
	}

	.option-link {
		font-size: 13px;
		color: var(--im-color-primary);
		text-decoration: none;
		transition: opacity 0.2s ease;

		&:hover {
			opacity: 0.85;
		}
	}

	.submit-btn {
		width: 100%;
		height: 46px;
		margin-top: 8px;
		border-radius: 10px;
		border: none;
		font-size: 15px;
		font-weight: 600;
		letter-spacing: 0.5px;
		transition: transform 0.2s ease, box-shadow 0.2s ease;

		&:hover {
			transform: translateY(-1px);
			box-shadow: 0 8px 20px rgba(65, 105, 222, 0.28);
		}
	}

	.demo-login-link {
		display: block;
		margin-top: 12px;
		text-align: center;
		text-decoration: none;
		color: var(--im-color-primary);
		font-size: 13px;

		&:hover {
			opacity: 0.85;
		}
	}

	.form-footer {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 6px;
		max-width: 380px;
		width: 100%;
		margin: 24px auto 0;
		padding-top: 20px;
		border-top: 1px solid #eef2f7;
	}

	.footer-text {
		font-size: 13px;
		color: var(--im-text-color-lighter);
	}

	.footer-link {
		font-size: 13px;
		font-weight: 600;
		color: var(--im-color-primary);
		text-decoration: none;
		transition: opacity 0.2s ease;

		&:hover {
			opacity: 0.85;
		}
	}
}
</style>
